/**
 * ServerWakingBanner — top-of-page banner shown while the admin-app's fetch
 * wrapper is retrying through a Railway cold-start.
 *
 * Architecture:
 *   - Subscribes to `onRetryStatus` from lib/api.ts (the same pub/sub the
 *     `request<T>` wrapper notifies on retry attempts).
 *   - Stays invisible until the retry takes >2s — fast-recovery retries
 *     (e.g. 1 retry + 200ms response) don't flash a banner.
 *   - After 10s of being VISIBLE, swaps to softer "still warming up" copy
 *     (anxiety reduction — the user knows we're still waiting).
 *   - Dismisses smoothly via CSS fade within 500ms once the wrapper emits
 *     a `resolved` phase event.
 *
 * Why a top-banner rather than the toast stack:
 *   - On a real cold-start (50-90s observed 2026-05-11) the user sees the
 *     dashboard skeleton, then a banner — they understand the state without
 *     thinking "is this broken?"
 *   - A toast in the top-right disappears after 3s; the banner persists for
 *     the duration of the wakeup so the user has a stable anchor.
 *
 * AC reference: parallel-sessions-2026-05-11/PROMPT-C-cold-start-retry.md
 * § Story C2 ServerWakingBanner UX.
 */

import { useEffect, useRef, useState } from 'react';
import { onRetryStatus, type RetryStatus } from '../lib/api.js';

/** ms after a retry chain begins before the banner appears. */
const REVEAL_DELAY_MS = 2_000;
/** ms after the banner becomes visible before copy softens to "almost there". */
const ESCALATE_AFTER_VISIBLE_MS = 10_000;
/** ms for the CSS fade-out before unmount. Keep in sync with the CSS rule. */
const FADE_OUT_MS = 500;

const COPY_INITIAL = 'Spinning up the server...';
const COPY_ESCALATED = 'Still warming up... almost there';

type Phase = 'hidden' | 'pending' | 'visible' | 'fading';

interface BannerState {
  readonly phase: Phase;
  readonly escalated: boolean;
}

const INITIAL_STATE: BannerState = { phase: 'hidden', escalated: false };

export function ServerWakingBanner(): JSX.Element | null {
  const [state, setState] = useState<BannerState>(INITIAL_STATE);

  // Timer refs survive re-renders without retriggering the subscription effect.
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const escalateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chainActiveRef = useRef<boolean>(false);

  useEffect(() => {
    const clearReveal = (): void => {
      if (revealTimerRef.current) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
    const clearEscalate = (): void => {
      if (escalateTimerRef.current) {
        clearTimeout(escalateTimerRef.current);
        escalateTimerRef.current = null;
      }
    };
    const clearFade = (): void => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };

    const handleRetrying = (): void => {
      // Multiple `retrying` events fire per chain (one per retry attempt).
      // We only arm the reveal timer on the FIRST event — the banner's
      // lifecycle is per-chain, not per-attempt.
      if (chainActiveRef.current) return;
      chainActiveRef.current = true;
      clearFade();

      setState({ phase: 'pending', escalated: false });
      revealTimerRef.current = setTimeout(() => {
        revealTimerRef.current = null;
        setState({ phase: 'visible', escalated: false });
        // 10s after the banner is shown, escalate copy.
        escalateTimerRef.current = setTimeout(() => {
          escalateTimerRef.current = null;
          setState((s) =>
            s.phase === 'visible' ? { ...s, escalated: true } : s,
          );
        }, ESCALATE_AFTER_VISIBLE_MS);
      }, REVEAL_DELAY_MS);
    };

    const handleResolved = (): void => {
      const wasActive = chainActiveRef.current;
      chainActiveRef.current = false;
      clearReveal();
      clearEscalate();

      if (!wasActive) return;

      setState((s) => {
        // If never made it past `pending`, hide silently — no fade flash.
        if (s.phase !== 'visible') return INITIAL_STATE;

        fadeTimerRef.current = setTimeout(() => {
          fadeTimerRef.current = null;
          setState(INITIAL_STATE);
        }, FADE_OUT_MS);
        return { ...s, phase: 'fading' };
      });
    };

    const unsubscribe = onRetryStatus((status: RetryStatus) => {
      if (status.phase === 'retrying') handleRetrying();
      else if (status.phase === 'resolved') handleResolved();
    });

    return () => {
      unsubscribe();
      clearReveal();
      clearEscalate();
      clearFade();
    };
  }, []);

  if (state.phase === 'hidden' || state.phase === 'pending') return null;

  const message = state.escalated ? COPY_ESCALATED : COPY_INITIAL;
  const className =
    state.phase === 'fading'
      ? 'ferro-server-waking-banner ferro-server-waking-banner--fading'
      : 'ferro-server-waking-banner ferro-server-waking-banner--visible';

  // Per AC C2: clicking the banner is a no-op. We deliberately do NOT
  // render a close button — the retry loop owns the lifecycle.
  return (
    <div
      role="status"
      aria-live="polite"
      className={className}
      data-testid="server-waking-banner"
    >
      <span className="ferro-server-waking-banner__spinner" aria-hidden="true" />
      <span className="ferro-server-waking-banner__message">{message}</span>
    </div>
  );
}
